import {
  createWheelGestureState,
  wheelGestureMode,
} from "../StageWheelGesture/gesture";
import { canPan, publishPan } from "../StageManualPan/pan";
import { createStagePinch, type LockedWheelZoomHandler } from "./pinch";

export type { LockedWheelZoomHandler };
import {
  applyDragToViewTransform,
  applyWheelPanToViewTransform,
  applyWheelToViewTransform,
  canStartTimelinePanDrag,
  type TimelineViewTransform,
} from "../ViewTransform/transform";

interface DragSession {
  view: TimelineViewTransform;
  pointerX: number;
  pointerY: number;
}

export interface StageManualViewOptions {
  allowWheelZoom: boolean;
  /** Live read so a lock change does not drop the gesture. */
  getAllowPanDrag: () => boolean;
  /** True when a pan should leave follow mode. */
  getLockedPan: () => boolean;
  getCameraLock: () => boolean;
  onLockedPan?: (next: TimelineViewTransform) => void;
  onLockedWheelZoom?: LockedWheelZoomHandler;
  onManualZoom?: () => void;
  getView: () => TimelineViewTransform;
  setView: (next: TimelineViewTransform) => void;
  dragRef: { current: DragSession | null };
}

export function attachStageManualViewListeners(
  stage: HTMLElement,
  options: StageManualViewOptions,
): () => void {
  const stageSize = (): { w: number; h: number } => {
    const rect = stage.getBoundingClientRect();
    return { w: rect.width, h: rect.height };
  };

  const pointerInStage = (event: { clientX: number; clientY: number }) => {
    const rect = stage.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const wheelGesture = createWheelGestureState();
  const pinch = createStagePinch(options, stage);

  const onWheel = (event: WheelEvent): void => {
    if (!options.allowWheelZoom) {
      return;
    }
    event.preventDefault();
    const { w, h } = stageSize();
    if (w < 1 || h < 1) {
      return;
    }
    const mode = wheelGestureMode(wheelGesture, event);
    if (mode === "pending") {
      return;
    }
    if (mode === "pan") {
      const next = applyWheelPanToViewTransform(
        options.getView(),
        w,
        h,
        event.deltaX,
      );
      publishPan(options, next);
      return;
    }
    const factor = Math.exp(-event.deltaY * 0.002);
    const pointer = pointerInStage(event);
    if (options.getCameraLock()) {
      options.onLockedWheelZoom?.(factor, pointer.x, pointer.y, w, h);
    } else {
      options.setView(applyWheelToViewTransform(
        options.getView(), w, h, pointer.x, pointer.y, factor,
      ));
    }
    options.onManualZoom?.();
  };

  const endDrag = (pointerId: number): void => {
    if (options.dragRef.current === null) {
      return;
    }
    stage.releasePointerCapture(pointerId);
    options.dragRef.current = null;
    stage.classList.remove("timeline-stage--dragging");
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (pinch.onDown(event, pointerInStage(event))) {
      return;
    }
    // With the camera locked, one finger flicks between scenes instead. Unlock it to pan.
    if (event.pointerType === "touch" && options.getCameraLock()) {
      return;
    }
    if (
      !canPan(options) ||
      event.button !== 0 ||
      !canStartTimelinePanDrag(event.target)
    ) {
      return;
    }
    const { w, h } = stageSize();
    if (w < 1 || h < 1) {
      return;
    }
    const pointer = pointerInStage(event);
    options.dragRef.current = {
      view: { ...options.getView() },
      pointerX: pointer.x,
      pointerY: pointer.y,
    };
    stage.setPointerCapture(event.pointerId);
    stage.classList.add("timeline-stage--dragging");
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (pinch.onMove(event.pointerId, pointerInStage(event), stageSize())) {
      return;
    }
    const session = options.dragRef.current;
    if (session === null) {
      return;
    }
    const { w, h } = stageSize();
    const pointer = pointerInStage(event);
    publishPan(
      options,
      applyDragToViewTransform(
        session.view,
        session.pointerX,
        session.pointerY,
        pointer.x,
        pointer.y,
        w,
        h,
      ),
    );
  };

  const onPointerDone = (event: PointerEvent): void => {
    pinch.onDone(event.pointerId);
    endDrag(event.pointerId);
  };

  stage.addEventListener("wheel", onWheel, { passive: false });
  stage.addEventListener("pointerdown", onPointerDown);
  stage.addEventListener("pointermove", onPointerMove);
  stage.addEventListener("pointerup", onPointerDone);
  stage.addEventListener("pointercancel", onPointerDone);

  return () => {
    stage.removeEventListener("wheel", onWheel);
    stage.removeEventListener("pointerdown", onPointerDown);
    stage.removeEventListener("pointermove", onPointerMove);
    stage.removeEventListener("pointerup", onPointerDone);
    stage.removeEventListener("pointercancel", onPointerDone);
    stage.classList.remove("timeline-stage--dragging");
  };
}
